/**
 * ThreadV5ConditionPivotTable
 *
 * Condition-pivot UX for the V5 versioned views (FXC-8608).
 *
 * Port-vs-extend decision: this component extends the LIVE V5 data path
 * (fetchRunTablePaginated → RunTableData) rather than reviving the orphaned
 * V2 DataTable.  The V2 DataTable was built against a mock TableRow/ColumnDef
 * shape and depended on v2-workspace contexts (WorkspaceStore, PlotDataContext)
 * that are not mounted in the V5 shell.  Extending the V5 path means the
 * pivot table lives on the same data that every other V5 surface sees, and
 * reuses the AG Grid instance that is already embedded in AdvancedRunsTable.
 *
 * Stubbed fields (pending Shuttle backend — FXC-8608 backend slice):
 *   - _source / solver / fidelity_level — these are DROPPED by get_flat_table
 *     today.  The component renders a "Source" group column when ANY of those
 *     fields are present on a row; it degrades gracefully (no column shown)
 *     when they are absent.  Shuttle will add source-emission; no frontend
 *     change will be required beyond that.
 */

import { memo, useCallback, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ColGroupDef, GridReadyEvent } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";

import { fetchRunTablePaginated, type RunTableData } from "../../api/runTable";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Condition fields that can be used as pivot axes. */
export type PivotAxis = "alpha" | "beta" | "rpm";

export interface ConditionPivotState {
  /** The field the user has selected as the primary row-grouping pivot. */
  primaryAxis: PivotAxis;
  /** When set, only show rows matching this alpha (tolerance ±CONDITION_TOL). */
  filterAlpha: number | null;
  /** When set, only show rows matching this beta (tolerance ±CONDITION_TOL). */
  filterBeta: number | null;
}

interface Props {
  projectId: string;
  branch?: string;
  isVisible?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Half-width of the numeric equality window for condition matching. */
const CONDITION_TOL = 0.01;

/** Fields that map to "alpha" — backend sends alpha_deg, frontend aliases to alpha. */
const ALPHA_FIELDS = ["alpha", "alpha_deg"] as const;

/** Fields that map to "beta" — same alias pattern. */
const BETA_FIELDS = ["beta", "beta_deg"] as const;

/**
 * Source / solver / fidelity fields emitted by the Shuttle backend slice
 * (FXC-8608 backend).  None of these exist yet in get_flat_table — the UI
 * degrades gracefully when absent.
 */
const SOURCE_FIELDS = ["_source", "solver", "fidelity_level"] as const;

const AXIS_LABELS: Record<PivotAxis, string> = {
  alpha: "α (deg)",
  beta: "β (deg)",
  rpm: "RPM",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function firstNumber(row: Record<string, unknown>, fields: readonly string[]): number | null {
  for (const f of fields) {
    const v = row[f];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string") {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return null;
}

/** Read the best available rpm value.  Converts bet_omega (rad/s) → rpm when needed. */
function rpmFromRow(row: Record<string, unknown>): number | null {
  // First look for a direct rpm field
  for (const f of ["RPM", "propeller_rpm", "rpm"] as const) {
    const v = row[f];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string") {
      const n = parseFloat(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  // Fallback: convert bet_omega rad/s → rpm
  const omega = row["bet_omega"];
  if (typeof omega === "number" && !Number.isNaN(omega)) {
    return (omega * 60) / (2 * Math.PI);
  }
  return null;
}

/**
 * Normalise rows: add _alpha_norm, _beta_norm, _rpm_norm synthetic fields
 * so downstream pivot logic has a single stable field name to reference.
 */
function normaliseRow(row: Record<string, unknown>): Record<string, unknown> {
  const enriched: Record<string, unknown> = { ...row };
  const alpha = firstNumber(row, ALPHA_FIELDS);
  if (alpha !== null) enriched["_alpha_norm"] = alpha;
  const beta = firstNumber(row, BETA_FIELDS);
  if (beta !== null) enriched["_beta_norm"] = beta;
  const rpm = rpmFromRow(row);
  if (rpm !== null) enriched["_rpm_norm"] = Math.round(rpm);

  // Source field normalisation (degrades gracefully when absent)
  for (const f of SOURCE_FIELDS) {
    if (row[f] != null) {
      enriched["_source_norm"] = row[f];
      break;
    }
  }
  return enriched;
}

/** Collect the distinct numeric values for a pivot axis across all rows. */
function pivotValues(rows: Record<string, unknown>[], axis: PivotAxis): number[] {
  const field = `_${axis}_norm` as const;
  const seen = new Set<number>();
  for (const r of rows) {
    const v = r[field];
    if (typeof v === "number") seen.add(v);
  }
  return [...seen].sort((a, b) => a - b);
}

/** Filter rows to those matching the given condition tolerances. */
function applyConditionFilter(
  rows: Record<string, unknown>[],
  filterAlpha: number | null,
  filterBeta: number | null,
): Record<string, unknown>[] {
  return rows.filter((r) => {
    if (filterAlpha !== null) {
      const a = r["_alpha_norm"];
      if (typeof a !== "number" || Math.abs(a - filterAlpha) > CONDITION_TOL) return false;
    }
    if (filterBeta !== null) {
      const b = r["_beta_norm"];
      if (typeof b !== "number" || Math.abs(b - filterBeta) > CONDITION_TOL) return false;
    }
    return true;
  });
}

/** Return true if any row in the dataset carries a source-related field. */
function hasSourceField(rows: Record<string, unknown>[]): boolean {
  return rows.some((r) => r["_source_norm"] != null);
}

// ---------------------------------------------------------------------------
// Column builder
// ---------------------------------------------------------------------------

const QUANTITY_FIELDS = ["CL", "cl", "CD", "cd", "Cm", "cm", "cy", "cfz", "CY", "CFz", "ld", "l_d"];

function buildPivotColumnDefs(
  rows: Record<string, unknown>[],
  pivot: PivotAxis,
  showSource: boolean,
): (ColDef | ColGroupDef)[] {
  const conditionFields: ColDef[] = [
    {
      field: "_alpha_norm",
      headerName: "α (deg)",
      sortable: true,
      filter: "agNumberColumnFilter",
      minWidth: 90,
      enableRowGroup: true,
      initialRowGroup: pivot === "alpha",
      initialHide: pivot === "alpha",
      valueFormatter: (p) => (p.value != null ? String(Number(p.value).toFixed(2)) : "—"),
    },
    {
      field: "_beta_norm",
      headerName: "β (deg)",
      sortable: true,
      filter: "agNumberColumnFilter",
      minWidth: 90,
      enableRowGroup: true,
      initialRowGroup: pivot === "beta",
      initialHide: pivot === "beta",
      valueFormatter: (p) => (p.value != null ? String(Number(p.value).toFixed(2)) : "—"),
    },
    {
      field: "_rpm_norm",
      headerName: "RPM",
      sortable: true,
      filter: "agNumberColumnFilter",
      minWidth: 90,
      enableRowGroup: true,
      initialRowGroup: pivot === "rpm",
      initialHide: pivot === "rpm",
      valueFormatter: (p) => (p.value != null ? String(p.value) : "—"),
    },
  ];

  const quantityCols = QUANTITY_FIELDS.filter((f) =>
    rows.some((r) => typeof r[f] === "number"),
  ).map<ColDef>((f) => ({
    field: f,
    headerName: f.toUpperCase(),
    type: "numericColumn",
    aggFunc: f === "cd" || f === "CD" ? "min" : "avg",
    sortable: true,
    filter: "agNumberColumnFilter",
    minWidth: 100,
    valueFormatter: (p) =>
      p.value != null && typeof p.value === "number" ? p.value.toFixed(4) : "—",
  }));

  const groups: (ColDef | ColGroupDef)[] = [
    {
      headerName: "Conditions",
      headerClass: "v5-grid-group-conditions",
      marryChildren: true,
      children: conditionFields,
    } as ColGroupDef,
  ];

  if (quantityCols.length > 0) {
    groups.push({
      headerName: "Aerodynamic outputs",
      headerClass: "v5-grid-group-outputs",
      marryChildren: true,
      children: quantityCols,
    } as ColGroupDef);
  }

  if (showSource) {
    groups.push({
      field: "_source_norm",
      headerName: "Source / solver",
      enableRowGroup: true,
      sortable: true,
      minWidth: 140,
    } as ColDef);
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Chip button
// ---------------------------------------------------------------------------

function ConditionChip({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-testid={`condition-chip-${label}-${value}`}
      onClick={onClick}
      style={{
        padding: "2px 8px",
        borderRadius: 4,
        border: "1px solid var(--gray-a6)",
        fontSize: 11,
        cursor: "pointer",
        background: active ? "var(--accent-9)" : "var(--gray-a3)",
        color: active ? "white" : "var(--gray-12)",
        fontFamily: "var(--v5-mono, monospace)",
      }}
    >
      {label}={value}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function ConditionPivotTableImpl({ projectId, branch = "main", isVisible = true }: Props) {
  const [pivotState, setPivotState] = useState<ConditionPivotState>({
    primaryAxis: "alpha",
    filterAlpha: null,
    filterBeta: null,
  });

  // Reuse the same query key as DataExplorer so React Query de-dupes the request.
  const { data: raw, isLoading } = useQuery<RunTableData>({
    queryKey: ["run-table", projectId, branch, "full"],
    queryFn: () => fetchRunTablePaginated({ projectId, branch }),
    enabled: isVisible,
    placeholderData: keepPreviousData,
  });

  const normalisedRows = useMemo<Record<string, unknown>[]>(() => {
    if (!raw?.rows) return [];
    return raw.rows.map(normaliseRow);
  }, [raw?.rows]);

  const alphaValues = useMemo(() => pivotValues(normalisedRows, "alpha"), [normalisedRows]);
  const betaValues = useMemo(() => pivotValues(normalisedRows, "beta"), [normalisedRows]);
  const rpmValues = useMemo(() => pivotValues(normalisedRows, "rpm"), [normalisedRows]);

  const filteredRows = useMemo(
    () => applyConditionFilter(normalisedRows, pivotState.filterAlpha, pivotState.filterBeta),
    [normalisedRows, pivotState.filterAlpha, pivotState.filterBeta],
  );

  const showSource = useMemo(() => hasSourceField(normalisedRows), [normalisedRows]);

  const columnDefs = useMemo(
    () => buildPivotColumnDefs(filteredRows, pivotState.primaryAxis, showSource),
    [filteredRows, pivotState.primaryAxis, showSource],
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        accentColor: "#4f9f77",
        backgroundColor: "rgba(250, 250, 247, 0.96)",
        borderColor: "rgba(29, 32, 29, 0.12)",
        browserColorScheme: "light",
        cellHorizontalPadding: 10,
        chromeBackgroundColor: "rgba(243, 242, 236, 0.96)",
        fontFamily: "var(--v5-mono, monospace)",
        foregroundColor: "#20231f",
        headerBackgroundColor: "rgba(237, 235, 228, 0.96)",
        headerFontSize: 11,
        oddRowBackgroundColor: "rgba(247, 246, 240, 0.82)",
        rowHoverColor: "rgba(79, 159, 119, 0.1)",
        selectedRowBackgroundColor: "rgba(79, 159, 119, 0.16)",
        wrapperBorderRadius: 0,
      }),
    [],
  );

  const gridRef = useRef<AgGridReact>(null);

  const handleGridReady = useCallback((_event: GridReadyEvent) => {
    // intentionally empty — future: restore filter model from URL
  }, []);

  const toggleAlpha = useCallback(
    (value: number) => {
      setPivotState((s) => ({
        ...s,
        filterAlpha: s.filterAlpha !== null && Math.abs(s.filterAlpha - value) < CONDITION_TOL ? null : value,
      }));
    },
    [],
  );

  const toggleBeta = useCallback(
    (value: number) => {
      setPivotState((s) => ({
        ...s,
        filterBeta: s.filterBeta !== null && Math.abs(s.filterBeta - value) < CONDITION_TOL ? null : value,
      }));
    },
    [],
  );

  const setPrimaryAxis = useCallback(
    (axis: PivotAxis) => setPivotState((s) => ({ ...s, primaryAxis: axis })),
    [],
  );

  if (isLoading) {
    return (
      <div style={{ padding: 16, color: "var(--gray-9)" }} data-testid="condition-pivot-loading">
        Loading condition pivot table...
      </div>
    );
  }

  if (!raw?.rows?.length) {
    return (
      <div style={{ padding: 16, color: "var(--gray-9)" }} data-testid="condition-pivot-empty">
        No run data available.
      </div>
    );
  }

  const btnStyle = (active: boolean): React.CSSProperties => ({
    padding: "2px 8px",
    borderRadius: 4,
    border: "1px solid var(--gray-a6)",
    fontSize: 12,
    cursor: "pointer",
    background: active ? "var(--accent-9)" : "var(--gray-a3)",
    color: active ? "white" : "var(--gray-12)",
  });

  return (
    <div
      style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}
      data-testid="condition-pivot-table"
    >
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "4px 12px",
          borderBottom: "1px solid var(--gray-a5)",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 11, color: "var(--gray-10)", fontWeight: 600 }}>Group by:</span>
        {(["alpha", "beta", "rpm"] as PivotAxis[]).map((axis) => (
          <button
            key={axis}
            type="button"
            data-testid={`pivot-axis-${axis}`}
            onClick={() => setPrimaryAxis(axis)}
            style={btnStyle(pivotState.primaryAxis === axis)}
          >
            {AXIS_LABELS[axis]}
          </button>
        ))}

        <div style={{ width: 1, height: 14, background: "var(--gray-a5)", margin: "0 4px" }} />

        {/* Alpha filter chips */}
        {alphaValues.length > 0 && (
          <>
            <span style={{ fontSize: 11, color: "var(--gray-9)" }}>Filter α:</span>
            {alphaValues.slice(0, 12).map((v) => (
              <ConditionChip
                key={v}
                label="α"
                value={v}
                active={pivotState.filterAlpha !== null && Math.abs(pivotState.filterAlpha - v) < CONDITION_TOL}
                onClick={() => toggleAlpha(v)}
              />
            ))}
            {alphaValues.length > 12 && (
              <span style={{ fontSize: 11, color: "var(--gray-9)" }}>+{alphaValues.length - 12} more</span>
            )}
          </>
        )}

        {/* Beta filter chips */}
        {betaValues.length > 1 && (
          <>
            <div style={{ width: 1, height: 14, background: "var(--gray-a5)", margin: "0 2px" }} />
            <span style={{ fontSize: 11, color: "var(--gray-9)" }}>β:</span>
            {betaValues.slice(0, 8).map((v) => (
              <ConditionChip
                key={v}
                label="β"
                value={v}
                active={pivotState.filterBeta !== null && Math.abs(pivotState.filterBeta - v) < CONDITION_TOL}
                onClick={() => toggleBeta(v)}
              />
            ))}
          </>
        )}

        {/* RPM summary (no per-value chip — too many values) */}
        {rpmValues.length > 0 && (
          <>
            <div style={{ width: 1, height: 14, background: "var(--gray-a5)", margin: "0 2px" }} />
            <span style={{ fontSize: 11, color: "var(--gray-9)" }}>
              {rpmValues.length} RPM{rpmValues.length !== 1 ? "s" : ""}
            </span>
          </>
        )}

        {/* Source notice */}
        {!showSource && (
          <span
            style={{ fontSize: 10, color: "var(--gray-7)", marginLeft: "auto", fontStyle: "italic" }}
            data-testid="source-field-absent-notice"
          >
            Source grouping pending backend (Shuttle FXC-8608)
          </span>
        )}

        <span style={{ fontSize: 11, color: "var(--gray-8)", marginLeft: showSource ? "auto" : 0 }}>
          {filteredRows.length} runs{pivotState.filterAlpha !== null || pivotState.filterBeta !== null ? " (filtered)" : ""}
        </span>
      </div>

      {/* AG Grid */}
      <div style={{ flex: 1, minHeight: 0, width: "100%" }} data-testid="condition-pivot-grid">
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          rowData={filteredRows}
          columnDefs={columnDefs}
          getRowId={(params) => String(params.data._node_id ?? params.data.runId ?? Math.random())}
          groupDisplayType="singleColumn"
          groupDefaultExpanded={1}
          autoGroupColumnDef={{
            headerName: `${AXIS_LABELS[pivotState.primaryAxis]} (grouped)`,
            minWidth: 180,
            cellRendererParams: { suppressCount: false },
          }}
          rowGroupPanelShow="always"
          suppressAggFuncInHeader={false}
          animateRows={false}
          onGridReady={handleGridReady}
          defaultColDef={{
            cellDataType: false,
            resizable: true,
            sortable: true,
            filter: true,
          }}
          statusBar={{
            statusPanels: [
              { statusPanel: "agTotalAndFilteredRowCountComponent" },
              { statusPanel: "agAggregationComponent" },
            ],
          }}
        />
      </div>
    </div>
  );
}

/**
 * Memoized: `projectId`/`branch`/`isVisible` are the only props, and this AG-Grid-backed pivot
 * lives as a SIBLING panel to the Design page's live 2D section — every drag/animation frame that
 * changes `config` re-renders the whole `HighliftMultifidelitySurface` tree, and without this memo
 * this table (an expensive AgGridReact instance) re-ran its full render on every one of those frames
 * even though none of ITS OWN props ever changed (FXC-9009 — measured via React Profiler as the
 * single largest contributor to "animate deploy" jank: ~26ms average, 200ms+ spikes, out of a ~40ms
 * total frame cost, while not even reading `config`).
 */
export const ConditionPivotTable = memo(ConditionPivotTableImpl);
